import * as moment from 'moment';
import {IImomoResponse, IDataValue, IDischargeModel} from '../../api/api.types';
import {IStationApi} from '../../api/station-api.service';
import {IParsedKN15Telegram} from './kn15-telegram/kn15-parse.service';
import {IProcessedDataRow, IProcessedDataCell} from './save.controller';
import {getDischargeValue} from '../../discharge/utils';

export interface IWaterLevelTrendResponse {
  previousWaterLevelAtEight: number;
  valid: boolean;
}

export interface IPartialDailyData {
  date: moment.Moment;
  morningWaterLevel?: number;
  morningDischarge?: number;
  eveningWaterLevel?: number;
  averageWaterLevel?: number;
}

export interface IOperationalInputService {
  verifyWaterTrend(date: moment.Moment,
                   stationCode: string,
                   waterLevelAtEight: number,
                   waterLevelTrend: number): ng.IPromise<IWaterLevelTrendResponse>;
  calculateAverageWaterLevel(waterLevelAtTwenty: number,
                             previousWaterLevelAtEight: number): number;
  calculateDailyDischarge(waterLevelData: IPartialDailyData[],
                          dischargeModel: IDischargeModel,
                          previousDischargeModel: IDischargeModel): IProcessedDataRow[];
}

export class OperationalInputService implements IOperationalInputService {
  static $inject = ['$http', 'backendUrl', 'stationApi'];

  private dataValuesEndpoint: string;
  private dischargeModelsEndpoint: string;

  constructor(private $http: ng.IHttpService, backendUrl: string, private stationApi: IStationApi) {
    this.dataValuesEndpoint = `${backendUrl}/data_values`;
    this.dischargeModelsEndpoint = `${backendUrl}/discharge_models`;
  }

  verifyWaterTrend(date: moment.Moment, stationCode: string,
                   waterLevelAtEight: number,
                   waterLevelTrend: number) {
    const previousDate = moment(date).subtract(1, 'day');
    return this.$http<IImomoResponse<IDataValue>>({
      url: `${this.dataValuesEndpoint}/water_level`,
      method: 'GET',
      params: {
        localDateTime: previousDate.unix(),
        utcOffset: previousDate.utcOffset() * 60,
        siteCode: stationCode
      }
    }).then((httpData) => {
      const dataValues = httpData.data.resources;
      if (dataValues && dataValues.length) {
        const waterLevelDataValue = dataValues[0];
        const measuredTrend = waterLevelAtEight - waterLevelDataValue.dataValue;
        const isValidTrend = measuredTrend == waterLevelTrend;
        return {
          previousWaterLevelAtEight: waterLevelDataValue.dataValue,
          valid: isValidTrend
        };
      }
      return null;
    });
  }

  calculateAverageWaterLevel(waterLevelAtTwenty: number,
                             waterLevelAtEight: number) {
    // if no water level at eight, do not calculate average
    if (waterLevelAtEight != null) {
      return Math.round((waterLevelAtTwenty +
          waterLevelAtEight) / 2);
    } else {
      return null;
    }
  }

  calculateDailyDischarge(waterLevelData: IPartialDailyData[],
                          dischargeModel: IDischargeModel,
                          previousDischargeModel: IDischargeModel): IProcessedDataRow[] {
    const dischargeModelDate = dischargeModel.validFrom ? moment.unix(dischargeModel.validFrom) : null;
    return waterLevelData.map(waterLevelDatum => {
      const currentDate = waterLevelDatum.date;
      let morningCell: IProcessedDataCell = null;
      let eveningCell: IProcessedDataCell = null;
      let averageCell: IProcessedDataCell = null;
      if (!dischargeModelDate || currentDate.isAfter(dischargeModelDate, 'day')) {
        if (waterLevelDatum.morningWaterLevel != null) {
          morningCell = {
            waterLevel: waterLevelDatum.morningWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.morningWaterLevel,
                dischargeModel),
            highlighted: true
          };
        }
        if (waterLevelDatum.eveningWaterLevel != null) {
          eveningCell = {
            waterLevel: waterLevelDatum.eveningWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.eveningWaterLevel,
                dischargeModel),
            highlighted: true
          };
        }
        if (waterLevelDatum.averageWaterLevel != null) {
          averageCell = {
            waterLevel: waterLevelDatum.averageWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.averageWaterLevel,
                dischargeModel),
            highlighted: true
          };
        }
      } else if (currentDate.isSame(dischargeModelDate, 'day')) {
        let dischargeSum = 0;
        let dischargeCount = 0;
        if (waterLevelDatum.morningDischarge != null &&
            waterLevelDatum.morningWaterLevel != null) {
          dischargeSum += waterLevelDatum.morningDischarge;
          dischargeCount++;
          morningCell = {
            waterLevel: waterLevelDatum.morningWaterLevel,
            discharge: waterLevelDatum.morningDischarge,
            highlighted: false
          };
        }
        if (waterLevelDatum.morningDischarge == null &&
            waterLevelDatum.morningWaterLevel != null) {
          morningCell = {
            waterLevel: waterLevelDatum.morningWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.morningWaterLevel,
              dischargeModel),
            highlighted: false
          };
        }
        if (waterLevelDatum.eveningWaterLevel != null) {
          const discharge = getDischargeValue(waterLevelDatum.eveningWaterLevel,
              dischargeModel);
          dischargeSum += discharge;
          dischargeCount++;
          eveningCell = {
            waterLevel: waterLevelDatum.eveningWaterLevel,
            discharge,
            highlighted: true
          };
        }
        averageCell = {
          waterLevel: null,
          discharge: dischargeCount ? dischargeSum / dischargeCount : null,
          highlighted: true
        };
      } 
      // when telegram date equals model date
      // previous day should be calculated with previous telegram
      else {
        if (waterLevelDatum.morningWaterLevel != null) {
          morningCell = {
            waterLevel: waterLevelDatum.morningWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.morningWaterLevel,
                previousDischargeModel),
            highlighted: true
          };
        }
        if (waterLevelDatum.eveningWaterLevel != null) {
          eveningCell = {
            waterLevel: waterLevelDatum.eveningWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.eveningWaterLevel,
                previousDischargeModel),
            highlighted: true
          };
        }
        if (waterLevelDatum.averageWaterLevel != null) {
          averageCell = {
            waterLevel: waterLevelDatum.averageWaterLevel,
            discharge: getDischargeValue(waterLevelDatum.averageWaterLevel,
                previousDischargeModel),
            highlighted: true
          };
        }
      }
      return {
        date: currentDate,
        morningCell,
        eveningCell,
        averageCell
      };
    });
  }
}



/** WEBPACK FOOTER **
 ** ./app/operational/input/input.service.ts
 **/